import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { Header } from '../layout/Header';
import { Footer } from '../layout/Footer';
import { Section } from '../ui/Section';
import { Button } from '../ui/Button';
import { Gallery } from '../ui/Gallery';
import { ContactCTA } from '../sections/ContactCTA';
import { Service, galleryImages } from '../../data/mock';

interface ServicePageProps {
  service: Service;
}

export const ServicePageLayout = ({ service }: ServicePageProps) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url("${service.image}")`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
          }}
        >
          <div className="absolute inset-0 bg-primary-dark/80" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-white text-center pt-20">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
            {service.title}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto leading-relaxed animate-fade-in-up delay-100">
            {service.description}
          </p>
        </div>
      </div>

      <main className="flex-grow">
        {/* Description & Features */}
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-secondary font-bold uppercase tracking-widest mb-2 text-sm">Detalji Usluge</h2>
              <h3 className="text-3xl font-bold mb-6 text-gray-900">
                Vrhunski Kvalitet i <br />
                <span className="text-primary">Profesionalna Izrada</span>
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                {service.longDescription || service.description}
              </p>
              
              <div className="bg-gray-50 p-8 rounded-xl border border-gray-100">
                <h4 className="font-bold text-xl mb-4 text-gray-900">Karakteristike</h4>
                <ul className="space-y-3">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700">
                      <CheckCircle2 className="text-secondary shrink-0 mt-1" size={18} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="space-y-6">
              {service.benefits?.map((benefit, index) => (
                <div key={index} className="flex gap-4 p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:border-secondary/30 transition-colors">
                  <div className="w-12 h-12 bg-primary/5 rounded-full flex items-center justify-center text-primary shrink-0 font-bold text-lg">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2 text-gray-900">{benefit.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              ))}
              
              <div className="bg-primary text-white p-8 rounded-xl text-center mt-8">
                <h4 className="font-bold text-xl mb-4">Zainteresovani ste?</h4>
                <p className="mb-6 text-blue-100">Kontaktirajte nas za besplatnu procenu i ponudu.</p>
                <Link to="/kontakt">
                  <Button variant="white" fullWidth>
                    Zatraži Ponudu <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Section>

        {/* Gallery */}
        <Section background="gray">
          <div className="container mx-auto">
            <Gallery 
              images={galleryImages} 
              category={service.id} 
              title={`Galerija - ${service.title}`} 
            />
          </div>
        </Section>

        <ContactCTA />
      </main>

      <Footer />
    </div>
  );
};
